﻿/*
 * Purpose: Render presentational controls for phone number input and optional country selector.
 * Boundary: Presentational only; no local state or side effects.
 * Invariants:
 * - Country selector displays flag and dial code; dropdown lists all available countries.
 * - Input is disabled if no country is selected; enabled if default or selected country is provided.
 * - Flags load from /assets/flags/{flag}.svg.
 * - ASCII only; no Unicode characters in comments or strings.
 */

// Component CountrySelector:
// Stateless selector showing flag, dial code, and optional dropdown
export function CountrySelector({
    selectedCountry,
    enableCountrySelector,
    isDropdownOpen,
    onToggleDropdown,
    filteredCountries,
    onCountrySelect,
    searchTerm,
    setSearchTerm,
    dropdownRef,
}) {
    // Rendering - country selector with flag display and optional dropdown
    return (
        <div className="relative inline-block py-1 flex-shrink-0" ref={dropdownRef}>
            {/* Trigger - flag and dial code display */}
            <div
                className={`flex items-center ${enableCountrySelector ? 'cursor-pointer' : 'cursor-default'
                    } px-1 py-2 md:px-3 md:py-2 rounded-lg md:rounded-l-lg md:rounded-r-none bg-neutral-900 ring-1 ring-neutral-800 focus:ring-emerald-600 w-16 md:w-24`}
                onClick={enableCountrySelector ? onToggleDropdown : undefined}
            >
                {selectedCountry ? (
                    <>
                        <img
                            src={`/assets/flags/${selectedCountry.flag}.svg`}
                            alt={`${selectedCountry.name} flag`}
                            className="w-4 h-3 mr-1.5 md:w-5 md:h-4 md:mr-2 flex-shrink-0"
                            crossOrigin="anonymous"
                        />
                        <span className="whitespace-nowrap text-xs md:text-sm">{selectedCountry.dialCode}</span>
                    </>
                ) : (
                    <span className="text-neutral-500">...</span>
                )}
            </div>

            {/* Dropdown - search input and scrollable country list */}
            {enableCountrySelector && isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-64 bg-neutral-900 border border-neutral-800 rounded-md shadow-lg">
                    {/* Search input */}
                    <div className="p-2 border-b border-neutral-800">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search country or code..."
                            className="w-full px-2 py-1 text-sm bg-neutral-800 rounded border border-neutral-700 outline-none focus:border-emerald-600"
                            autoFocus
                        />
                    </div>
                    {/* Country list */}
                    <div className="max-h-64 overflow-y-auto">
                        {filteredCountries.map((country) => (
                            <div
                                key={country.code}
                                className="flex items-center px-3 py-2 hover:bg-neutral-800 cursor-pointer"
                                onClick={() => onCountrySelect(country)}
                            >
                                <img
                                    src={`/assets/flags/${country.flag}.svg`}
                                    alt={`${country.name} flag`}
                                    className="w-5 h-4 mr-3 flex-shrink-0"
                                    crossOrigin="anonymous"
                                />
                                <span className="w-14 flex-shrink-0 text-sm tabular-nums">{country.dialCode}</span>
                                <span className="text-sm">{country.name}</span>
                            </div>
                        ))}
                        {filteredCountries.length === 0 && (
                            <div className="px-3 py-2 text-sm text-neutral-500">No matches</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Component PhoneInput:
// Stateless numeric input styled to match AuthForm controls
export function PhoneInput({ value, onChange, disabled }) {
    // Rendering - text input for phone number entry
    return (
        <input
            type="tel"
            value={value}
            onChange={onChange}
            disabled={disabled}
            className="flex-1 min-w-0 px-1 py-2 md:px-3 md:py-2 bg-neutral-900 rounded-lg md:rounded-r-lg md:rounded-l-none ring-1 ring-neutral-800 focus:ring-emerald-600 outline-none"
            placeholder="Enter number"
            required
        />
    );
}